import { useCallback, useEffect, useRef, useState, MutableRefObject } from 'react';
import { tasksService } from '@/services/api/tasks.service';
import { Task } from '@/types/task.types';
import { format, addDays } from 'date-fns';
import { useCalendarStore } from '@/store/calendar.store';

// Semaphore to limit concurrent API requests globally
class ConcurrencySemaphore {
  private queue: Array<() => void> = [];
  private running = 0;

  constructor(private maxConcurrent: number) {}

  async acquire(): Promise<void> {
    if (this.running < this.maxConcurrent) {
      this.running++;
      return;
    }
    return new Promise(resolve => this.queue.push(resolve));
  }

  release(): void {
    this.running--;
    const next = this.queue.shift();
    if (next) {
      this.running++;
      next();
    }
  }

  get activeCount(): number {
    return this.running;
  }
}

// Global semaphore: max 2 concurrent calendar API requests
const calendarApiSemaphore = new ConcurrencySemaphore(2);

interface UseProgressiveCalendarTasksOptions {
  enablePolling?: boolean;
  pollingInterval?: number; // in milliseconds
}

interface UseProgressiveCalendarTasksReturn {
  tasks: Task[];
  isLoadingBackground: boolean;
  error: Error | null;
  loadedRanges: Array<{ start: Date; end: Date }>;
  fetchAdditionalRange: (start: Date, end: Date) => Promise<void>;
  clearCache: () => void;
  lastRefresh: Date | null;
  nextRefresh: Date | null;
  // Exposed for optimistic updates integration
  tasksMapRef: MutableRefObject<Map<string, Task>>;
  setTasks: React.Dispatch<React.SetStateAction<Task[]>>;
  refreshAllRanges: () => Promise<void>;
  // Blacklist for tasks being deleted (prevents refresh from re-adding them)
  addToDeleteBlacklist: (id: string) => void;
  removeFromDeleteBlacklist: (id: string) => void;
}

/**
 * Hook for progressive loading of calendar tasks
 * Maintains all loaded tasks in memory and fetches additional ranges on demand
 * Supports automatic refresh with polling
 */
export function useProgressiveCalendarTasks(
  options: UseProgressiveCalendarTasksOptions = {}
): UseProgressiveCalendarTasksReturn {
  const { 
    enablePolling = true,
    pollingInterval = 2 * 60 * 1000 // Default: 2 minutes when active
  } = options;

  // Get current date from calendar store instead of using new Date()
  const { currentDate } = useCalendarStore();

  // Polling intervals
  const ACTIVE_INTERVAL = pollingInterval; // 2 minutes when active
  const INACTIVE_INTERVAL = 10 * 60 * 1000; // 10 minutes when inactive
  const REACTIVATION_THRESHOLD = 2 * 60 * 1000; // 2 minutes - refresh if inactive for this long

  // Map to store all tasks by ID for efficient deduplication
  const tasksMapRef = useRef<Map<string, Task>>(new Map());
  const [tasks, setTasks] = useState<Task[]>([]);
  const [isLoadingBackground, setIsLoadingBackground] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [loadedRanges, setLoadedRanges] = useState<Array<{ start: Date; end: Date }>>([]);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [nextRefresh, setNextRefresh] = useState<Date | null>(null);

  // Track ongoing fetches to prevent duplicate requests
  const ongoingFetchesRef = useRef<Set<string>>(new Set());
  const pollingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<Date>(new Date());
  const isTabActiveRef = useRef<boolean>(true);

  // Blacklist for tasks being deleted (prevents refresh from re-adding them)
  const deleteBlacklistRef = useRef<Set<string>>(new Set());

  // Burst detection for rapid navigation (prevents excessive API calls)
  const navigationCountRef = useRef<number>(0);
  const lastNavigationTimeRef = useRef<number>(0);
  const navigationDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const BURST_THRESHOLD_MS = 500; // Time window to detect burst
  const BURST_DEBOUNCE_MS = 300; // Delay after burst before executing

  // AbortController to cancel ongoing requests when navigation changes
  const abortControllerRef = useRef<AbortController | null>(null);
  const navigationIdRef = useRef<number>(0); // Track navigation ID to cancel stale requests

  /**
   * Create a unique key for a date range
   */
  const getRangeKey = (start: Date, end: Date): string => {
    return `${format(start, 'yyyy-MM-dd')}_${format(end, 'yyyy-MM-dd')}`;
  };

  /**
   * Check if a range is already loaded or being loaded
   */
  const isRangeLoaded = useCallback((start: Date, end: Date): boolean => {
    const key = getRangeKey(start, end);
    if (ongoingFetchesRef.current.has(key)) {
      return true; // Consider as loaded if fetch is in progress
    }
    
    // Check if this range overlaps with any loaded range
    return loadedRanges.some(range => {
      // Range is considered loaded if it overlaps significantly
      return start >= range.start && end <= range.end;
    });
  }, [loadedRanges]);

  /**
   * Fetch tasks for a specific date range and merge with existing
   * Uses semaphore to limit concurrent requests
   * Supports navigation ID to cancel stale requests
   */
  const fetchAdditionalRange = useCallback(async (
    start: Date,
    end: Date,
    forceRefresh = false,
    navigationId?: number
  ) => {
    const rangeKey = getRangeKey(start, end);

    // Prevent duplicate fetches
    if (!forceRefresh && ongoingFetchesRef.current.has(rangeKey)) {
      console.log('[Progressive] Fetch already in progress for', rangeKey);
      return;
    }

    // Check if range is already loaded (skip check if forcing refresh)
    if (!forceRefresh && isRangeLoaded(start, end)) {
      console.log('[Progressive] Range already loaded', rangeKey);
      return;
    }

    // Check if this request is stale (navigation has changed)
    if (navigationId !== undefined && navigationId !== navigationIdRef.current) {
      console.log('[Progressive] Skipping stale request for', rangeKey, '(nav:', navigationId, 'current:', navigationIdRef.current, ')');
      return;
    }

    // Acquire semaphore slot (limits concurrent requests)
    console.log(`[Progressive] Waiting for semaphore (${calendarApiSemaphore.activeCount}/2 active)...`);
    await calendarApiSemaphore.acquire();

    // Double-check staleness after acquiring semaphore
    if (navigationId !== undefined && navigationId !== navigationIdRef.current) {
      console.log('[Progressive] Request became stale while waiting, releasing semaphore');
      calendarApiSemaphore.release();
      return;
    }

    ongoingFetchesRef.current.add(rangeKey);
    setIsLoadingBackground(true);
    setError(null);

    // Start time to ensure minimum visible duration
    const startTime = Date.now();

    try {
      const formattedStartDate = format(start, 'yyyy-MM-dd');
      const formattedEndDate = format(end, 'yyyy-MM-dd');

      console.log('[Progressive] Fetching range:', formattedStartDate, 'to', formattedEndDate);
      const response = await tasksService.getCalendarTasks(formattedStartDate, formattedEndDate);

      // Check staleness again after API call
      if (navigationId !== undefined && navigationId !== navigationIdRef.current) {
        console.log('[Progressive] Discarding stale response for', rangeKey);
        return;
      }

      if (response.success) {
        // Merge new tasks with existing ones, but filter out blacklisted tasks
        response.data.tasks.forEach(task => {
          // Don't add tasks that are being deleted
          if (!deleteBlacklistRef.current.has(task.id)) {
            tasksMapRef.current.set(task.id, task);
          }
        });

        // Update tasks array from map
        const allTasks = Array.from(tasksMapRef.current.values());
        setTasks(allTasks);

        // Track this loaded range
        setLoadedRanges(prev => {
          // Merge overlapping ranges
          const newRanges = [...prev, { start, end }];
          return mergeOverlappingRanges(newRanges);
        });

        // Update last refresh time
        setLastRefresh(new Date());

        console.log(`[Progressive] Loaded ${response.data.tasks.length} tasks, total: ${allTasks.length}`);
      } else {
        throw new Error('Failed to fetch calendar tasks');
      }
    } catch (err) {
      console.error('[Progressive] Error fetching range:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
      // No toast - errors are handled gracefully (existing data preserved)
      // The error is logged in console for debugging
    } finally {
      // Ensure minimum visible duration for loading state (300ms)
      const elapsed = Date.now() - startTime;
      if (elapsed < 300) {
        await new Promise(resolve => setTimeout(resolve, 300 - elapsed));
      }

      ongoingFetchesRef.current.delete(rangeKey);
      setIsLoadingBackground(false);
      calendarApiSemaphore.release();
    }
  }, [isRangeLoaded]);

  /**
   * Refresh visible range around current date (for polling)
   * - Only refreshes ±45 days to avoid massive queries
   * - Keeps existing data if refresh fails (rollback strategy)
   * - Preserves tasks outside the refresh window
   */
  const refreshAllRanges = useCallback(async () => {
    if (loadedRanges.length === 0) {return;}

    // Only refresh a reasonable window around the current date (90 days total)
    // This prevents massive queries like 7+ months of data
    const refreshStart = addDays(currentDate, -45);
    const refreshEnd = addDays(currentDate, 45);

    console.log(`[Progressive] Refreshing visible range: ${format(refreshStart, 'yyyy-MM-dd')} to ${format(refreshEnd, 'yyyy-MM-dd')}`);

    // Save original data for rollback if refresh fails
    const originalMap = tasksMapRef.current;
    const originalTaskCount = originalMap.size;
    const newTasksMap = new Map<string, Task>();

    // Clear the ongoing fetches to force new requests
    ongoingFetchesRef.current.clear();

    // Temporarily swap the map reference to collect fresh data
    tasksMapRef.current = newTasksMap;

    try {
      await fetchAdditionalRange(refreshStart, refreshEnd, true); // Force refresh

      // Validate: only replace if we got meaningful data
      if (newTasksMap.size === 0) {
        console.warn('[Progressive] Refresh returned no data, keeping existing cache');
        tasksMapRef.current = originalMap;
        return;
      }

      // Merge: preserve tasks outside the refresh window from original data
      originalMap.forEach((task, id) => {
        const taskDate = task.workPeriod?.startDate ? new Date(task.workPeriod.startDate) : null;
        if (taskDate && (taskDate < refreshStart || taskDate > refreshEnd)) {
          // Task is outside refresh window - keep it if not already in new data
          if (!newTasksMap.has(id)) {
            newTasksMap.set(id, task);
          }
        }
      });

      // Update state with merged data
      tasksMapRef.current = newTasksMap;
      setTasks(Array.from(newTasksMap.values()));
      console.log(`[Progressive] Refresh complete: ${newTasksMap.size} tasks (was ${originalTaskCount})`);

    } catch (error) {
      // Refresh failed - rollback to original data
      console.error('[Progressive] Refresh failed, keeping existing cache:', error);
      tasksMapRef.current = originalMap;
    }
  }, [loadedRanges, fetchAdditionalRange, currentDate]);

  /**
   * Schedule next polling
   */
  const scheduleNextPoll = useCallback(() => {
    if (!enablePolling) {return;}
    
    // Clear existing timeout
    if (pollingTimeoutRef.current) {
      clearTimeout(pollingTimeoutRef.current);
    }
    
    // Determine interval based on tab activity
    const interval = isTabActiveRef.current ? ACTIVE_INTERVAL : INACTIVE_INTERVAL;
    const nextTime = new Date(Date.now() + interval);
    setNextRefresh(nextTime);
    
    pollingTimeoutRef.current = setTimeout(() => {
      refreshAllRanges();
      scheduleNextPoll(); // Reschedule
    }, interval);
    
    console.log(`[Progressive] Next refresh scheduled in ${interval / 1000}s (${isTabActiveRef.current ? 'active' : 'inactive'} mode)`);
  }, [enablePolling, ACTIVE_INTERVAL, INACTIVE_INTERVAL, refreshAllRanges]);

  /**
   * Handle tab visibility change
   */
  useEffect(() => {
    if (!enablePolling) {return;}
    
    const handleVisibilityChange = () => {
      const wasActive = isTabActiveRef.current;
      isTabActiveRef.current = !document.hidden;
      
      if (!wasActive && isTabActiveRef.current) {
        // Tab became active
        const inactiveDuration = Date.now() - lastActivityRef.current.getTime();
        
        if (inactiveDuration >= REACTIVATION_THRESHOLD) {
          // Inactive for 2+ minutes, refresh immediately (but smoothly)
          console.log('[Progressive] Tab reactivated after', Math.round(inactiveDuration / 1000), 'seconds - smooth refresh now');
          refreshAllRanges(); // This now keeps UI stable during refresh
        }
        
        // Reschedule with active interval
        scheduleNextPoll();
      } else if (wasActive && !isTabActiveRef.current) {
        // Tab became inactive
        lastActivityRef.current = new Date();
        // Reschedule with inactive interval
        scheduleNextPoll();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enablePolling, REACTIVATION_THRESHOLD, refreshAllRanges, scheduleNextPoll]);

  /**
   * Initial load: Progressive strategy for fast UX
   * Phase 1: Load ±7 days from currentDate (not today!) - displayed immediately
   * Phase 2: Load extended ranges in background (smooth navigation)
   *
   * Uses navigationId to cancel stale requests when user navigates rapidly
   * Uses semaphore to limit concurrent API requests to 2
   */
  useEffect(() => {
    // Increment navigation ID to invalidate previous navigation's requests
    navigationIdRef.current++;
    const currentNavId = navigationIdRef.current;

    // Burst detection logic
    const now = Date.now();
    const timeSinceLastNav = now - lastNavigationTimeRef.current;
    lastNavigationTimeRef.current = now;

    // Track navigation count within burst window
    if (timeSinceLastNav < BURST_THRESHOLD_MS) {
      navigationCountRef.current++;
    } else {
      navigationCountRef.current = 1;
    }

    // Function to execute the actual loading
    const executeLoad = () => {
      // Double-check this navigation is still current
      if (currentNavId !== navigationIdRef.current) {
        console.log('[Progressive] Navigation became stale, aborting load');
        return;
      }

      const baseDate = currentDate;
      const startTime = performance.now();

      // Phase 1: Fast initial load (±7 days from currentDate) - displayed immediately
      const quickStart = addDays(baseDate, -7);
      const quickEnd = addDays(baseDate, 7);

      console.log(`⚡ [Progressive] Nav #${currentNavId}: Loading visible period (±7 days from ${format(baseDate, 'yyyy-MM-dd')})...`);

      fetchAdditionalRange(quickStart, quickEnd, false, currentNavId).then(() => {
        // Check if still current navigation before continuing
        if (currentNavId !== navigationIdRef.current) {
          console.log(`[Progressive] Nav #${currentNavId} became stale, stopping phases`);
          return;
        }

        const phase1Duration = performance.now() - startTime;
        console.log(`✅ [Progressive] Nav #${currentNavId} Phase 1: Calendar ready in ${phase1Duration.toFixed(0)}ms`);

        // Phase 2: Load extended data in PARALLEL (both past and future)
        // Semaphore limits this to 2 concurrent requests anyway
        const pastStart = addDays(baseDate, -30);
        const pastEnd = addDays(baseDate, -7);
        const futureStart = addDays(baseDate, 7);
        const futureEnd = addDays(baseDate, 60);

        console.log(`🔄 [Progressive] Nav #${currentNavId} Phase 2: Loading extended ranges in parallel...`);

        Promise.all([
          fetchAdditionalRange(pastStart, pastEnd, false, currentNavId),
          fetchAdditionalRange(futureStart, futureEnd, false, currentNavId)
        ]).then(() => {
          if (currentNavId !== navigationIdRef.current) {
            return;
          }
          const totalElapsed = performance.now() - startTime;
          console.log(`🎉 [Progressive] Nav #${currentNavId}: All phases complete in ${totalElapsed.toFixed(0)}ms`);

          // Start polling after all data is loaded
          if (enablePolling) {
            scheduleNextPoll();
          }
        });
      });
    };

    // Clear any pending debounced execution
    if (navigationDebounceRef.current) {
      clearTimeout(navigationDebounceRef.current);
      navigationDebounceRef.current = null;
    }

    // Burst detection: first 2 clicks execute immediately, 3+ are debounced
    if (navigationCountRef.current >= 3) {
      console.log(`⏳ [Progressive] Burst detected (${navigationCountRef.current} rapid navigations), debouncing nav #${currentNavId}...`);
      navigationDebounceRef.current = setTimeout(() => {
        // Check again if still current navigation
        if (currentNavId === navigationIdRef.current) {
          console.log(`[Progressive] Burst ended, executing nav #${currentNavId}`);
          executeLoad();
        }
      }, BURST_DEBOUNCE_MS);
    } else {
      // Execute immediately for first 2 navigations
      executeLoad();
    }

    // Cleanup on unmount or before next effect
    return () => {
      if (navigationDebounceRef.current) {
        clearTimeout(navigationDebounceRef.current);
      }
    };
  }, [currentDate, fetchAdditionalRange, scheduleNextPoll, enablePolling, BURST_THRESHOLD_MS, BURST_DEBOUNCE_MS]); // Re-run when currentDate changes from store
  
  /**
   * Cleanup polling on unmount
   */
  useEffect(() => {
    return () => {
      if (pollingTimeoutRef.current) {
        clearTimeout(pollingTimeoutRef.current);
      }
    };
  }, []);

  /**
   * Clear all cached data
   */
  const clearCache = useCallback(() => {
    tasksMapRef.current.clear();
    setTasks([]);
    setLoadedRanges([]);
    ongoingFetchesRef.current.clear();
    deleteBlacklistRef.current.clear();
  }, []);

  /**
   * Add a task ID to the delete blacklist
   * Prevents the task from being re-added during refresh
   */
  const addToDeleteBlacklist = useCallback((id: string) => {
    deleteBlacklistRef.current.add(id);
    console.log('[Progressive] Added to delete blacklist:', id);
  }, []);

  /**
   * Remove a task ID from the delete blacklist
   * Allows the task to be added again (after delete confirmed or rolled back)
   */
  const removeFromDeleteBlacklist = useCallback((id: string) => {
    deleteBlacklistRef.current.delete(id);
    console.log('[Progressive] Removed from delete blacklist:', id);
  }, []);

  return {
    tasks,
    isLoadingBackground,
    error,
    loadedRanges,
    fetchAdditionalRange,
    clearCache,
    lastRefresh,
    nextRefresh,
    // Expose internals for optimistic updates
    tasksMapRef,
    setTasks,
    refreshAllRanges,
    // Blacklist management
    addToDeleteBlacklist,
    removeFromDeleteBlacklist
  };
}

/**
 * Merge overlapping date ranges
 */
function mergeOverlappingRanges(ranges: Array<{ start: Date; end: Date }>): Array<{ start: Date; end: Date }> {
  if (ranges.length <= 1) {return ranges;}
  
  // Sort ranges by start date
  const sorted = [...ranges].sort((a, b) => a.start.getTime() - b.start.getTime());
  
  const merged: Array<{ start: Date; end: Date }> = [sorted[0]];
  
  for (let i = 1; i < sorted.length; i++) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    
    // Check if ranges overlap or are adjacent (within 1 day)
    if (current.start <= addDays(last.end, 1)) {
      // Merge by extending the end date if necessary
      last.end = new Date(Math.max(last.end.getTime(), current.end.getTime()));
    } else {
      // No overlap, add as new range
      merged.push(current);
    }
  }
  
  return merged;
}